/*
 * Copyright (C) 2021 Quartet Health, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import { t } from '@lingui/macro';

/**
 * @description The `t` macro requires the exported copy
 * to be a function. Otherwise those who import `t` will
 * get `t` binded to i18n singleton upon initialization.
 * (Most likely defaults to en-US).
 */
const medicaidCopy = (): any => ({
  rightCare: {
    title: t`We'll find you the right care`,
    subtitle: t`We'll match you with care that's right for you, whatever your health needs and goals.`,
    card: [
      {
        title: t`Talk therapy`,
        paragraph: t`We work with trusted therapists who treat a range of conditions. Our providers can meet in person or over the phone. You'll work with your therapist to set goals and solve problems. You can also learn how to change behavior and manage your feelings.`
      },
      {
        title: t`Medication management`,
        paragraph: t`See a psychiatrist to help with your medication. Our doctors can meet in person or over the phone. You may also need to see a therapist to talk about what you're going through.`,
        italicNote: t`Please note: some medications can only be prescribed in person.`
      },
      {
        title: t`Finding you the right care`,
        paragraph: t`No matter what you're looking for, Quartet will help you find the right care. If you're not sure what's right for you, we'll help you understand your options. If it's not a good fit, let us know and we'll find you another.`
      }
    ],
    getStarted: t`Get started with Quartet`
  },

  landing: {
    cta: {
      title: t`Care that's right for you`,
      subtitle: t`Your mental health matters. We'll find you the right care that's covered by your insurance plan.`,
      button: t`Get started`
    },

    heroImageAltText: t`A smiling woman`,

    header: {
      seeIfImCovered: t`Find my match`
    },

    scrollHeader: {
      seeIfImCovered: t`Get Started`
    }
  },

  howQuartetWorks: {
    title: t`How Quartet works`,
    getStarted: t`Get started`,
    insurance: {
      title: t`Verify your insurance`,
      paragraph: t`We're currently working with insurance plans in CA, IL, LA, NC, NJ, PA, and WA to match patients to the right care.`
    },
    yourself: {
      title: t`Tell us about yourself`,
      paragraph: t`Answer a few short questions about your health and what you're looking for so we can find the care that's right for you.`
    },
    care: {
      title: t`Be matched to care, for free`,
      paragraph: t`We'll match you to the right mental healthcare provider based on your needs, preferences and insurance.`
    },
    provider: {
      title: t`Connect with your provider`,
      paragraph: t`Your matched provider will reach out to set up an appointment. Please note that co-pays or deductibles apply if you see a provider.`
    }
  },

  getStarted: {
    title: t`Get started in 5 minutes`,
    subtitle: t`Quartet is a free service. We'll match you with care that's covered by your insurance plan.`,
    getStarted: t`Get started`,
    cards: [
      {
        title: t`Tell us a little about yourself`,
        paragraph: t`Answer a few questions about your health goals and what you're looking for. If you're not sure, don't worry - we're here to help.`
      },
      {
        title: t`Sign up for a Quartet account`,
        paragraph: t`Sign up to take a health assessment and get updates about your match. You can also securely message with your Quartet Care Navigator.`
      },
      {
        title: t`We'll find you the right care`,
        paragraph: t`We'll match you with one of our trusted care options. Your Quartet Care Navigator will be there for you, every step of the way.`
      }
    ]
  },

  mentalHealth: {
    title: t`It's okay if you don't feel okay`,
    paragraph1: t`If you're feeling worried, stressed, angry or sad, that's okay. Sometimes you can feel that way after a big life change, like moving, losing a job, the death of a loved one, or having children. Sometimes you can feel that way for no reason.`,
    paragraph2: t`Talking to someone can help. Quartet will find you the right care.`
  },

  whatToExpect: {
    title: t`What to expect`,
    getStarted: t`Get started with Quartet`,
    appointment: {
      title: t`The first appointment`,
      paragraph: t`This is a time where you can get to know your provider and share more about your health. If it doesn't feel like a good fit, let us know and we'll match you to another.`
    },
    goal: {
      title: t`Setting goals`,
      paragraph: t`Your provider will work with you to set goals for what you'd like to achieve. Some examples of goals include: working through grief, better managing stress, or increasing patience.`
    },
    care: {
      title: t`Continuing care`,
      paragraph: t`How long you continue care is up to you and your provider. As you begin to reach your treatment goals, you can choose to self-manage or continue care to reach new goals.`
    }
  },

  testimonial: {
    quote: t`Dealing with mental health can be hard, but the right person can help you address anxiety, anger, and fear, and develop coping skills to get through the toughest parts.`,
    author: 'larue',
    alt: t`A woman smiling`
  },

  faq: {
    title: t`Frequently Asked Questions`,
    item1: {
      question: t`Is Quartet a mental health provider?`,
      answer: t`Quartet does not provide direct mental health care. We are a free service that works to find you care based on your needs and insurance plan.`
    },
    item2: {
      question: t`How will Quartet match me with the right care?`,
      answer: t`We use your health goals, insurance, and address to match you with a mental health provider or program.`
    },
    item3: {
      question: t`What if my provider or program isn’t the right fit?`,
      answer: t`If the provider or program we match you with isn’t quite right, don't worry. Let us know, and we’ll work to find you a better match.`
    },
    item4: {
      question: t`Who's eligible for Quartet services?`,
      answer: t`Quartet partners with select insurance plans to find you mental health care that's in your network.`
    }
  },

  resources: {
    affiliateNotInScope: {
      modalTitle: t`Our curated provider options`,
      modalCopy1: t`Quartet only refers patients to licensed providers who have demonstrated the ability to meet a wide range of patient clinical needs, treat patients with various levels of acuity, have the capacity to service new patients, and meet your preference for virtual or in-person treatment.`,
      modalCopy2: t`Certain providers are offered the opportunity to participate in Quartet's affiliate program ("Affiliates") where, for a small fee, Quartet provides these providers with administrative and referral services and rigourously vets them to ensure they possess and maintain an even higher level of clinical capability, which includes the uniform practice of evidence-based treatment. While at all times maintaining patient confidentiality, Quartet regularly monitors all associated providers - Affiliates and non-Affiliates - to ensure that they deliver consistently high levels of care.`,
      modalCopy3: t`We exclude providers from participating if their clinical and operational practices fall outside of Quartet's standard of care, if they have demonmstrated poor operational performance, or if they have been subject to substantiated disciplinary actions on their licenses, including exclusion from federal programs.`,
      modalButton: t`Yes, I understand`
    },
    nextSteps: {
      heading: t`Thank you,`,
      subtitle: t`We've sent your appointment request to your selected practice. You can expect to hear from us in the next few days! Your care is important to us so in the event your requested practice does not work out, we will automatically match you to your next best match or we will reach out if we need more information.`
    }
  },

  footer: {
    title: t`EMERGENCY SUPPORT`,
    paragraph: t`If you are in a crisis or if you or any other person may be in danger, don't use this site - `,
    call: t`call 911`,
    resource: t`These resources`,
    resourceUrl: t`https://patient.quartethealth.com/p/crisis-resources`,
    resourceLabel: t`These resources can also provide you with immediate help`,
    resourceHelp: t`can also provide you with immediate help`,
    privacy: t`Privacy`,
    terms: t`Terms of Service`,
    termsLabel: t`Terms of Service`,
    security: t`Security`,
    securityLabel: t`Security`,
    language: t`Notice of Nondiscrimination`,
    languageLabel: t`Notice of Nondiscrimination`,
    provider: t`For Providers`,
    providerLabel: t`For Providers`
  },

  selectInsurance: {
    title: t`Select your insurance`,
    subTitle1: t`If you're not a member of a participating insurance plan and you're interested in Quartet, `,
    linkText: t`sign up here`,
    subTitle2: t` and we'll notify you when we expand our services.`
  },

  disclaimer: {
    subTitle1: t`Quartet prioritizes security to protect your health information. To learn more, take a look at our `,
    privacy: t`privacy policy `,
    privacyUrl: 'https://www.quartethealth.com/privacy-policy',
    subTitle2: t` and `,
    terms: t`terms of use`,
    termsUrl: 'https://www.quartethealth.com/terms-of-service'
  },

  insuranceDisclaimer: null,

  yourself: {
    title: t`Tell us about yourself`,
    subTitle: t`Let us know who you are.`,
    pediatricTitle: t`Tell us about the patient`,
    pediatricSubtitle: `Let us know who the patient is.`,
    firstName: t`First name`,
    lastName: t`Last name`,
    preferredName: t`Preferred first name`,
    dateOfBirth: t`Date of birth`,
    gender: t`Gender`,
    addressSubtitle: t`We'll use this address to match you with providers nearby.`,
    pediatricAddressSubtitle: t`We'll use this address to match the patient with providers nearby.`,
    streetAddress: t`Address`,
    streetAddressLineTwo: t`Apt., unit, or suite # (Optional)`,
    city: t`City`,
    state: t`State`,
    postalCode: t`Zip code`,
    disclaimer: t`Currently, our providers are meeting by phone or video chat. We'll find you a provider close to you so you'll have the option to meet in person.`,
    pediatricDisclaimer: t`Currently, our providers are meeting by phone or video chat. We'll find them a provider close so they'll have the option to meet in person.`,
    optional: t`(optional)`
  },

  contactInfo: {
    title: t`Contact info`,
    subtitle: t`We'll use this to contact you with information about your match.`,
    email: t`Email`,
    optional: t`(optional)`,
    OptumEmailDisclaimerTitle: t`Quartet can send me emails.`,
    OptumEmailDisclaimerBody: t`I agree to receive emails from Quartet, and acknowledge the emails may contain my health information which may indirectly relate to sensitive services like mental health and substance use disorder care. These emails will be sent unencrypted. There is a risk of interception or disclosure of the contents of these emails. I understand I can withdraw my consent at any time.`,
    OptumEmailDisclaimerError: t`In order to receive emails, you must agree to the terms below`,
    phone: t`Phone number`,
    sms: t`Quartet can send me SMS messages about my mental health care.`,
    smsDisclaimer: t`Text HELP for help or STOP to cancel messages at any time. Msg and data rates may apply. Msg frequency varies.`,
    altSms: t`Quartet can send my parent/legal guardian SMS messages about my mental health care.`,
    optumSmsDisclaimerBody: t`I acknowledge and agree that these text messages, which may contain Protected Health Information (PHI), will be sent via unencrypted means and there is some risk of disclosure or interception of the messages.`,
    termsUrl: t`https://www.quartethealth.com/terms-of-service`,
    voicemail: t`Quartet can leave detailed voicemails.`,
    voicemailDisclaimer: t`This includes specific information about mental health providers and care.`,
    altTitle: t`Parent/legal guardian contact info (Optional)`,
    altTitleNotOptional: t`Parent/legal guardian contact info`,
    altSubtitle: t`Enter details of the patient's legal guardian who is authorized to coordinate care. Quartet will use this individual as the primary point of contact.`,
    altRelation: t`Relation to patient`,
    altFirstName: t`First name`,
    altLastName: t`Last name`,
    altPhone: t`Phone number`
  },

  experience: {
    title: t`What are you currently experiencing?`,
    subTitle: t`Check all that apply. Select at least one option.`,
    pediatricTitle: t`What is the patient currently experiencing?`,
    depression: t`Depression`,
    anxiety: t`Anxiety or panic attacks`,
    adhd: t`ADHD`,
    ptsd: t`Post-traumatic stress disorder`,
    sleepDisorder: t`Sleeping too much or too little`,
    drugHistory: t`History of drug or alcohol use`,
    drugCurrent: t`Current drug or alcohol use`,
    hallucinations: t`Hearing or seeing things other people don't`,
    stress: t`Stress or big life changes`,
    moodSwings: t`Mood swings`,
    griefLoss: t`Grief/Loss`,
    eatingDisorder: t`Eating too much or too little`,
    familyIssues: t`Marriage or family problems`,
    abuseCurrent: t`Current sexual, emotional or physical abuse`,
    abuseHistory: t`History of sexual, emotional or physical abuse`,
    baby: t`Recently had a baby or currently pregnant`
  },

  patientPreferences: {
    title: t`What are you looking for in a provider?`,
    subtitle: t`We'll do our best to take your preferences into account.`,
    pediatricTitle: t`What is the patient looking for in a provider?`,
    pediatricSubtitle: t`We'll do our best to take their preferences into account.`,
    telePreference: t`How would you like to meet with your provider?`,
    pediatricTelePreference: t`How would they like to meet with their provider?`,
    inPerson: t`In-person`,
    inPersonDesc: t`You'll visit a provider's office for one-on-one therapy.`,
    pediatricInPersonDesc: t`They'll visit a provider's office for one-on-one therapy.`,
    teleOnly: t`Virtually`,
    teleOnlyDesc: t`You'll meet with a provider using video services, like Zoom or Skype.`,
    pediatricTeleOnlyDesc: t`They'll meet with a provider using video services, like Zoom or Skype.`,
    teleAndInPerson: t`In-person and virtually`,
    teleAndInPersonDesc: t`You'll have the option to visit a provider's office and use video services.`,
    pediatricTeleAndInPersonDesc: t`They'll have the option to visit a provider's office and use video services.`,
    noPreference: t`No preference`,
    telePreferenceError: t`Please select an option`,
    genderPreference: t`Preferred provider gender`,
    genderError: t`Select a gender`,
    languagePreference: t`Preferred provider language`,
    languageError: t`Select a language`,
    optional: t`(optional)`
  },

  service: {
    title: t`What kind of care are you looking for?`,
    pediatricTitle: t`What kind of care is the patient looking for?`,
    insuranceSelectorInstructions: t`Confirm your insurance to see options covered by your plan.`,
    pediatricInsuranceSelectorInstructions: t`Confirm their insurance to see options covered by their plan.`,
    subTitle: t`Check all that apply. Select at least one option.`,
    disclaimer: t`COVID-19 update: Quartet will match you with a provider who can meet by phone or video chat.`,
    pediatricDisclaimer: t`COVID-19 update: Quartet will match them with a provider who can meet by phone or video chat.`,
    therapy: t`Talk Therapy`,
    medicationAndTherapy: t`Medication (may require talk therapy)`,
    drugsOrAlcohol: t`Medication-assisted treatment for drug or alcohol use`,
    unsure: t`I'm unsure. I'd like to talk with someone to learn more`
  },

  call: {
    title: t`When's the best time to call you?`,
    subTitle: t`Let us know the best time for your matched provider to get in touch.`,
    anytime: t`Any time`,
    morning: t`Morning (9am to 12pm)`,
    earlyAfternoon: t`Early afternoon (12pm to 3pm)`,
    lateAfternoon: t`Late afternoon (3pm to 6pm)`
  },

  welcome: {
    title: t`Welcome, `,
    paragraph1: t`Here's what to expect next:`,
    paragraph2: t`Your Quartet Care Navigator or your matched provider will call you. They'll get to know more about you, what you’re looking for, and answer any questions you may have.`,
    paragraph3: t`If you have any questions, feel free to call us at`,
    invalidMatches: t`We need to speak to you to better identify your care options and see if we can help. Talk to a Care Support Specialist now at ${0}, if we don't hear from you we will reach out directly.`,
    invalidForm: t`Something went wrong during submission. Please recheck the form and resubmit.`
  },

  learnMore: {
    title: t`Learn more about mental health and what you can expect from care`,
    button: t`Learn more`
  },

  changeState: {
    title: t`Update State`,
    paragraph: t`Please click Update State to change the patient's state of residence.`,
    cancelButton: t`Cancel`
  },

  dtm: {
    seekingCareFor: {
      address: {
        title: t`What is their address on file with their insurance?`,
        subheading: t`Please provide the address of the policy on file with their insurance.`
      },
      dob: {
        title: t`When were they born?`,
        modalTitle: t`Why do we ask?`,
        modalCopy: t`We'll use your response to determine if you can refer on behalf of someone else to our services. Unfortunately, we do not offer our services for patients younger than 5 years old.`
      },
      genderIdentity: {
        title: t`Gender Identity`,
        subtitle: t`Which gender identity does the patient most identify with?`
      },
      sexBirth: {
        title: t`Sex At Birth`,
        subtitle: t`What was the patient's sex assigned at birth?`
      },
      pronouns: {
        title: t`Pronouns`,
        subtitle: t`What are the patient's pronouns?`
      },
      journey: {
        title: t`Journey`,
        main: t`What are they dealing with?`,
        subtitle: t`Answer some questions about the patient's mental health.`
      },
      whatBringsYouHere: {
        title: t`What brings you here for the patient today?`,
        selfTitle: t`What brings you here today?`,
        subtitle: t`Select the type of treatment the patient is looking for`,
        selfSubtitle: t`Select the type of treatment you are looking for`,
        sudSubtitle: t`What type of substance use treatment the patient is looking for?`,
        selfSudSubtitle: t`What type of substance use treatment are you looking for?`,
        talkTherapy: {
          title: t`Talk Therapy`,
          description_1: t`One-on-one counseing`,
          description_2: t`Meet with a licensed social worker, counselor or psychologist`,
          description_3: t`Often times this can be a good place to start`
        },
        groupTherapy: {
          title: t`Couples/Family Therapy`,
          description_1: t`Counseling with family members`,
          description_2: t`Relationships/couples therapy`,
          description_3: t`Note that all parties over the age of 18 need to consent`,
          shortDescription: t`Therapy with family member(s)`
        },
        medicationManagement: {
          title: t`Medication Managment`,
          description_1: t`Manage existing prescriptions`,
          description_2: t`To see if medication would be helpful`,
          description_3: t`Meet with a licensed medication provider`,
          shortDescription: t`Meet with a medication provider`
        },
        diagnosis: {
          title: t`Evaluation/Diagnosis`,
          description_1: t`ADD/ADHD Evaluation`,
          description_2: t`Autism Evaluation`,
          description_3: t`Medical Documentation`,
          description_4: t`Bariatric Assessment`,
          shortDescription: t`ADHD/ADD, Autism, Bariatric`
        },
        substanceUse: {
          general: {
            title: t`Substance Use Treatment`,
            description_1: t`Any substance/alcohol use impacting daily functioning`,
            description_2: t`Varying treatment levels`,
            description_3: t`Medication Assisted Treatment`,
            shortDescription: t`Varying levels of treatment for substance use`
          },
          outpatientTreatment: {
            title: t`Outpatient Treatment`,
            description_1: t`Similar to traditional talk therapy`,
            description_2: t`Sessions are focused on topics related to substance/alcohol use`,
            description_3: t`Session are typically held once a week`,
            shortDescription: t`Substance use counseling`
          },
          intensiveOutpatientTreatment: {
            title: t`Intensive Outpatient Treatment`,
            description_1: t`Treatment is non-residential and does not support detox`,
            description_2: t`Sessions are typically formatted to include both individual and group therapy`,
            description_3: t`Program is typical several hours/day on several days for several weeks.`,
            shortDescription: t`More extensive/frequent counseling`
          },
          inpatientTreatment: {
            title: t`Inpatient Treatment`,
            description_1: t`Treatment is residential and can safely support detox`,
            description_2: t`This is a good option for those needing a higher level of care than what outpatient can offer`,
            shortDescription: t`Inpatient/Residential treatment`
          },
          medicationAssisted: {
            title: t`Medication Assisted Treatment`,
            description_1: t`Uses medication in combination with counseling and behavioral therapies for the treatment of substance use`,
            description_2: t`Can ease withdrawal symptoms associated with alcohol or opioid dependence`,
            shortDescription: t`Medication to assist substance use`
          }
        }
      },
      primaryConcern: {
        title: t`What is the patient seeking services for?`,
        selfTitle: t`What are you seeking services for?`,
        subtitle: t`Select the main concern you would like us to take into account when finding your best match`,
        selfSubtitle: t`Select the main concern of the patient that you would like us to take into account when finding their best match`,
        placeholder: t`Primary Concern`,
        continue: t`Continue`
      },
      crisisPage: {
        title: t`Their safety is so important to us`,
        subtitle: t`<span>Many people experience this kind of thinking and we can help. However, <strong>Quartet is not a crisis intervention</strong>. If the patient is in emotional distress or thinking about hurting themselves or others, please make use of these resources:</span>`,
        resources: {
          local: t`<span>Visit a <strong>local resource</strong></span>`,
          text: t`<span>Text the <strong>Crisis Text Line</strong></span>`,
          call: t`<span>Call the <strong>National Suicide Prevention Lifeline</strong></span>`
        }
      },
      additionalConcerns: {
        title: t`Is there anything else the patient needs help with?`,
        selfTitle: t`Is there anything else you’d like help with?`,
        subtitle: t`elect any other concerns we should take into consideration when finding their best match`,
        selfSubtitle: t`Select other concerns you would like us to take into account when finding your best match`,
        placeholder: t`Start typing to add a concern`,
        continue: t`Continue`,
        skip: t`Skip`
      },
      thankYou: {
        title: t`Thank you for sharing those details with us.`,
        subtitle: t`The information that you provide will help us better match the patient to care.`
      },
      supportSeparator: {
        title: t`Support`,
        main: t`What kind of help are you looking for on behalf of the patient? `,
        subtitle: t`Help us understand what sort of treatment and providers are best for them.`
      },
      treatmentConsideration: {
        title: t`Treatment Consideration`,
        heading: t`What type of treatment/services are you considering for the patient?`,
        subtitle: t`Select all that apply`,
        bottomNote: t`If Couples / Family Therapy is selected, a Quartet Care Navigator will need to obtain verbal consent by phone from all parties over the age of 18 or not in guardianship of the patient prior to matching to a behavioral health provider.`,
        answerDiagnosis: t`For diagnosis`,
        answerMedication: t`To see if medication may be helpful`,
        answerTesting: t`For psychological / neuropsychological testing`,
        answerIndividual: t`Individual Therapy`,
        answerGroup: t`Therapy with Family and/or a partner`,
        answerStartMedication: t`Starting medication to support mental health`,
        answerCurrentMedication: t`Currently on medication that requires a new provider to prescribe`,
        answerDrugMedication: t`Medication to treat an alcohol or drug use disorder`,
        answerTreatmentOther: t`I'm unsure and would like to learn more`
      },
      treatmentMedium: {
        title: t`Treatment Medium`,
        heading: t`How would the patient like to meet with their provider?`,
        answerNoPreference: t`No Preference`,
        answerInPerson: t`In-person`,
        answerVirtually: t`Virtually`,
        answerBoth: t`Both, in-person and virtually`
      },
      addressValidation: {
        title: t`Provider Address Form`,
        addressLineOne: t`Address`,
        addressLineTwo: t`Apt., unit, or suite # (Optional)`,
        city: t`City`,
        postalCode: t`Zip Code`,
        heading: t`Is this the address you want us to use to locate a provider?`,
        subheading: t`We'll match the patient with a licensed provider in their state.`,
        errorAddress: t`Enter a street address`,
        errorAddress2: t`Cannot accept more than 255 characters`,
        errorCity: t`Enter a city`,
        errorPostalCode: t`Enter a valid zip code`,
        updateAddress: t`No, I have a different address for the search`
      },
      providerGenderIdentity: {
        title: t`Provider Gender Identity`,
        heading: t`Would the patient prefer a provider of a certain gender identity? `,
        answerMan: t`Man`,
        answerWoman: t`Woman`,
        answerTgMan: t`Transgender man`,
        answerTgWoman: t`Transgender woman`,
        answerNb: t`Non-binary or non-conforming`,
        answerNoPreference: t`No Preference`
      },
      providerLanguage: {
        title: t`Would you prefer a provider that speaks a different language?`,
        answerNoPreference: t`No Preference`
      }
    },
    master: {
      goBack: t`Back`,
      talkToHuman: t`Talk to a human`,
      humanModalSubtitle: t`Need to talk to someone right away? Give us a call at the number below to talk to someone immediately.`
    },
    start: {
      title: t`Let's get to know you`,
      subtitle: t`We'll ask some basic questions to get you the support you need.`
    },
    name: {
      title: t`What's your name?`,
      subheading: t`We'll use this to personalize your experience.`,
      preferredName: t`Preferred name (Optional)`
    },
    address: {
      title: t`What is the address on file with your insurance?`,
      subheading: t`Please provide the address of the policy holder on file with your insurance.`
    },
    addressValidation: {
      title: t`Provider Address Form`,
      addressLineOne: t`Address`,
      addressLineTwo: t`Apt., unit, or suite # (Optional)`,
      city: t`City`,
      postalCode: t`Zip Code`,
      heading: t`Is this the address you want us to use to locate a provider?`,
      subheading: t`Please provide the address you have on file with your insurance.`,
      errorAddress: t`Enter a street address`,
      errorAddress2: t`Cannot accept more than 255 characters`,
      errorCity: t`Enter a city`,
      errorPostalCode: t`Enter a valid zip code`,
      updateAddress: t`No, I have a different address for the search`
    },
    contactInfo: {
      title: t`Contact Info`,
      heading: t`And what's your contact information?`,
      subheading: t`It's our way to keep in touch and share care recommendations.`,
      email: t`Email`,
      phone: t`Phone number`,
      sms: t`Quartet can send me SMS messages about mental health care.`,
      voicemail: t`Quartet can leave detailed voicemails.`,
      voicemailDisclaimer: t`This includes specific information about mental health providers and care.`
    },
    seekingCareForPage: {
      title: t`Who are you seeking care for?`,
      myself: t`Myself`,
      myselfSubText: t`I'm seeking treatment for myself.`,
      someoneElse: t`Someone else`,
      someoneElseSubText: t`On behalf of a loved one`
    },
    trustedContact: {
      title: t`Trusted Contact`,
      heading: t`Would you like to provide a trusted contact who can help coordinate care on your behalf?`,
      answerYes: t`Yes`,
      answerNo: t`No`
    },
    patientName: {
      title: t`Loved One`,
      heading: t`Can you tells us a bit more about your loved one?`,
      firstName: t`Patient First Name`,
      lastName: t`Patient Last Name`,
      preferredName: t`Patient Preferred Name`
    },
    patientContactInfo: {
      title: t`Patient Contact Info`,
      heading: t`And what's the patient's contact information?`,
      subheading: t`It's our way to keep in touch and share care recommendations.`,
      sms: t`Quartet can send the patient SMS messages about mental health care.`,
      email: t`Email`,
      phone: t`Phone number`,
      voicemail: t`Quartet can leave detailed voicemails.`,
      voicemailDisclaimer: t`This includes specific information about mental health providers and care.`
    },
    altContact: {
      title: t`Trusted Contact Page`,
      headingBold: t`trusted contact `,
      subtitle: t`We will update them with information about how to best support you.`,
      firstName: t`First name`,
      lastName: t`Last name`,
      relationship: t`Relationship`,
      phone: t`Phone Number`
    },
    dob: {
      title: t`When were you born?`,
      modalTitle: t`Why do we ask?`,
      modalCopy: t`We'll use your response to determine if you can self-refer to our services. Unfortunately, we do not offer our services for patients younger than 5 years old.`
    },
    emailCapture: {
      title: t`Stay Updated`,
      heading: t`Stay in the loop`,
      subheading: t`Quartet is expanding quickly, and can let you know when our services will be available for you.`,
      emailLabel: t`Email`,
      emailError: t`Enter a valid email address`,
      signup: t`Sign up`
    },
    sexBirth: {
      title: t`Sex At Birth`,
      subtitle: t`What was your sex assigned at birth?`
    },
    pronouns: {
      title: t`Pronouns`,
      subtitle: t`What are your pronouns?`
    },
    genderIdentity: {
      title: t`Gender Identity`,
      subtitle: t`Which gender identity do you most identify with?`
    },

    Insurance: {
      title: t`Insurance`,
      confirmTitle: t`Confirm your insurance information`,
      completeTitle: t`Confirm and complete your insurance information`,
      insuranceNotFoundSubtitle: t`We confirmed you are eligible for Quartet's services. Please complete and confirm the information below`,
      confirmSubtitle: t`We found the information below in our records. Please check for accuracy. We'll collect additional health insurance plans next.`,
      completeSubtitle: t`We found the information below in our records. Please check for accuracy and provide the missing insurance card ID. We'll collect additional health insurance plans next.`,
      carrierPlaceholder: t`Insurance Carrier`,
      cardIdPlaceholder: t`Insurance Card ID`,
      groupIdPlaceholder: t`Group ID (Optional)`,
      insuranceIsCorrect: t`Yes, this is correct`,
      correctInsurance: t`No, let me correct it`
    },
    InsuranceModal: {
      addModal: t`Add health insurance`,
      editModal: t`Edit health insurance`,
      modalCopy: t`You can find this information on the front and back of your health insurance card`,
      carrierPlaceholder: t`Insurance carrier and plan`,
      cardIdPlaceholder: t`Insurance Card ID`,
      groupIdPlaceholder: t`Group ID (Optional)`,
      insurancePlanError: t`Select an insurance plan`,
      setPrimaryInsurance: t`Set as my primary health insurance`,
      cancelButton: t`Cancel`,
      saveButton: t`Save`
    },
    AdditionalInsurance: {
      documentTitle: t`Additional insurance`,
      title: t`Would you like to add an additional insurance plan?`,
      subtitle: t`While not common, some individuals may have multiple health insurance plans. Match results will run using the primary insurance plan indicated.`,
      addInsurance: t`Add additional insurance (optional)`,
      modalTitle: t`Learn more`,
      modalSubtitle: t`We encourage that you to confirm all insurance information with your matched provider directly and reach out to your insurance for any information regarding your copay or deductible.`
    },
    getSupport: {
      title: t`Get the support you need and deserve`,
      subtext: t`Although not a diagnosis, your answers suggest that you may need some help with`,
      subtext2: t`Below are your results. If these scores raise any concerns for you, sharing these results with someone you trust can be a great place to start.`,
      subtext3: t`Although not a diagnosis, below are your results. If these scores raise any concerns for you, sharing these results with someone you trust can be a great place to start.`,
      elevated: t`Elevated`,
      low: t`Low`,
      anxietyRisk: t`Anxiety risk: `,
      depressionRisk: t`Depression risk: `,
      substanceAbuseRisk: t`Substance use risk:`,
      treatmentOptions: t`See treatment options`
    },
    insurance: {
      title: t`Insurance`,
      heading: t`Insurance information`,
      subheading: t`We found the following insurance information in our system, please verify or correct any details.`,
      insurancePlanError: t`Please select an insurance plan`
    },
    loadingScope: {
      title: t`Loading Scope`,
      loading: t`Just a moment.`,
      subheading: t`We are checking to see if we have an existing relationship with your insurance plan.`,
      subheadingForPractice: t`We are checking to see if you have an active referral by your provider with Quartet.`
    },
    redirectInformation: {
      title: t`Next Steps`,
      foundInSystem: t`Looks like you're in our system! We've sent a code to the email and/or SMS provided to confirm your identity.`,
      informationCompleteForPatientApp: t`Looks like we have all the information needed. You can login or register for patient app to get updates on your referral to care.`
    },
    thanksForConfirming: {
      title: t`Thank you for confirming your information!`,
      subheading: t`As we continue to collect your information please note that we will share the details of your referral back with the referring Medical Practice.`,
      agree: t`I agree`
    },
    notInScope: {
      title: t`Next Steps`,
      sorry: t`We're sorry`,
      body: t`Unfortunately, we do not work with your health plan yet or we
              could not locate you in our member records. Please reach out
               to your plan by using the number on your member ID card for
                assistance finding care.`,
      description: t` For patients in ${0}, additional options for care may be available to you. Click here to see if one of the options meet your needs.`,
      wantToSignUp: t`Want to know when our services will be available for you? Sign up for updates below.`,
      signUp: t`Sign up`
    },
    welcomeName: {
      title: t`Welcome Name`
    },
    myStrength: {
      title: t`Sign Up for My Strength`,
      subtitle1: t`While we work to get you connected to care,`,
      subtitle2: t`build a healthier mind for a stronger you.`,
      info: t`Mental Health Coaching by Teladoc offers a flexible and digital program with proven tools to support many types of emotional and physical challenges.`,
      stress: t`Reducing stress`,
      sleep: t`Improving sleep`,
      depression: t`Managing depression`,
      anxiety: t`Managing anxiety`,
      mindfulness: t`Mindfulness & meditation`,
      emotions: t`Balancing intense emotions`,
      pregnancy: t`Pregnancy & early parenting`,
      pain: t`Managing chronic pain`,
      getStarted: t`Get started with Mental Health Coaching by Teladoc`,
      signUp: t`Sign Up`
    },
    whatBringsYouHere: {
      title: t`What brings you here for the patient today?`,
      selfTitle: t`What brings you here today?`,
      subtitle: t`Select the type of treatment the patient is looking for`,
      selfSubtitle: t`Select the type of treatment you are looking for`,
      sudSubtitle: t`What type of substance use treatment the patient is looking for?`,
      selfSudSubtitle: t`What type of substance use treatment are you looking for?`,
      talkTherapy: {
        title: t`Talk Therapy`,
        description_1: t`One-on-one counseing`,
        description_2: t`Meet with a licensed social worker, counselor or psychologist`,
        description_3: t`Often times this can be a good place to start`
      },
      groupTherapy: {
        title: t`Couples/Family Therapy`,
        description_1: t`Counseling with family members`,
        description_2: t`Relationships/couples therapy`,
        description_3: t`Note that all parties over the age of 18 need to consent`,
        shortDescription: t`Therapy with family member(s)`
      },
      medicationManagement: {
        title: t`Medication Managment`,
        description_1: t`Manage existing prescriptions`,
        description_2: t`To see if medication would be helpful`,
        description_3: t`Meet with a licensed medication provider`,
        shortDescription: t`Meet with a medication provider`
      },
      diagnosis: {
        title: t`Evaluation/Diagnosis`,
        description_1: t`ADD/ADHD Evaluation`,
        description_2: t`Autism Evaluation`,
        description_3: t`Medical Documentation`,
        description_4: t`Bariatric Assessment`,
        shortDescription: t`ADHD/ADD, Autism, Bariatric`
      },
      substanceUse: {
        general: {
          title: t`Substance Use Treatment`,
          description_1: t`Any substance/alcohol use impacting daily functioning`,
          description_2: t`Varying treatment levels`,
          description_3: t`Medication Assisted Treatment`,
          shortDescription: t`Varying levels of treatment for substance use`
        },
        outpatientTreatment: {
          title: t`Outpatient Treatment`,
          description_1: t`Similar to traditional talk therapy`,
          description_2: t`Sessions are focused on topics related to substance/alcohol use`,
          description_3: t`Session are typically held once a week`,
          shortDescription: t`Substance use counseling`
        },
        intensiveOutpatientTreatment: {
          title: t`Intensive Outpatient Treatment`,
          description_1: t`Treatment is non-residential and does not support detox`,
          description_2: t`Sessions are typically formatted to include both individual and group therapy`,
          description_3: t`Program is typical several hours/day on several days for several weeks.`,
          shortDescription: t`More extensive/frequent counseling`
        },
        inpatientTreatment: {
          title: t`Inpatient Treatment`,
          description_1: t`Treatment is residential and can safely support detox`,
          description_2: t`This is a good option for those needing a higher level of care than what outpatient can offer`,
          shortDescription: t`Inpatient/Residential treatment`
        },
        medicationAssisted: {
          title: t`Medication Assisted Treatment`,
          description_1: t`Uses medication in combination with counseling and behavioral therapies for the treatment of substance use`,
          description_2: t`Can ease withdrawal symptoms associated with alcohol or opioid dependence`,
          shortDescription: t`Medication to assist substance use`
        }
      }
    },
    primaryConcern: {
      title: t`What is the patient seeking services for?`,
      selfTitle: t`What are you seeking services for?`,
      subtitle: t`Select the main concern you would like us to take into account when finding your best match`,
      selfSubtitle: t`Select the main concern of the patient that you would like us to take into account when finding their best match`,
      placeholder: t`Primary Concern`,
      continue: t`Continue`
    },
    additionalConcerns: {
      title: t`Is there anything else the patient needs help with?`,
      selfTitle: t`Is there anything else you’d like help with?`,
      subtitle: t`elect any other concerns we should take into consideration when finding their best match`,
      selfSubtitle: t`Select other concerns you would like us to take into account when finding your best match`,
      placeholder: t`Start typing to add a concern`,
      continue: t`Continue`,
      skip: t`Skip`
    },
    planOrIntent: {
      title: t`Plan or Intent`,
      subtitle: t`You noted that you are experiencing recurring thoughts of hurting yourself or others, do you have intent or a plan?`,
      answerYes: t`Yes`,
      answerNo: t`No`
    },
    diagnosed: {
      title: t`Have you been diagnosed with any of the following?`,
      subtitle: t`Select all that apply`,
      adhd: t`Attention deficit/hyperactivity disorder`,
      bipolar: t`Bipolar disorder`,
      bpd: t`Borderline personality disorder`,
      ocd: t`Obsessive-compulsive disorder`,
      ptsd: t`Post-traumatic stress disorder (PTSD)`,
      psychosis: t`Psychosis`,
      schizophrenia: t`Schizophrenia`,
      severe: t`Severe depression`,
      never: t`Never been diagnosed`,
      diagnosedOther: t`Other`,
      modalTitle: t`Why do we ask?`,
      modalCopy: t`Depending on your answer, we'll match you to a care option/provider that can help with these symptoms and/or conditions.`,
      buttonText: t`Continue`
    },
    eligible: {
      title: t`You're Eligible for treatment!`,
      subtitle: t`We can service STATENAME`
    },
    isHorizonNonAdultPatient: {
      subtitle: t`Unfortunately, due to your insurance plan we do not accept patient initiated referrals for the age range provided. Please reach out to your plan by using the number on your insurance card for assistance finding mental health care.`
    },
    isNoInsuranceCarrier: {
      subtitle: t`Unfortunately, due to your insurance plan we do not offer we do not accept patient initiated referrals. Please reach out to your primary care provider for assistance finding mental health care.`
    },
    ineligibleArea: {
      subtitle: t`Unfortunately, we do not offer Quartet services to you at this time. Please reach out to your plan by using the number on your insurance card for assistance finding mental health care.`
    },
    ineligibleInsurance: {
      subtitle: t`Unfortunately, due to your insurance plan we do not offer patient initiated referrals. Please reach out to your plan by using the number on your insurance card for assistance finding mental health care.`
    },
    ineligibleAge: {
      subtitle: t`Unfortunately, we do not offer our services to patients within the age range provided. Please reach out to your Primary Care Provider or use the number on your insurance card for assistance finding mental health care.`
    },
    ineligibleMatches: {
      subtitle: t`Unfortunately given your location, clinical requests, and preferences we are finding limited matches. We want to ensure we find the best match for you, a member of our Care Navigation team will be in touch with you shortly to assist you and discuss your care options.`
    },
    outOfScopeIneligibleAgeInMarket: {
      subtitle: t`Unfortunately, we do not offer our services to patients within the age range provided. Please reach out to your Primary Care Provider or use the number on your insurance card for assistance finding mental health care.`
    },
    ineligibleAgeInMarket: {
      subtitle: t`Unfortunately, due to the age range provided, we require that a parent or guardian submit the referral on your behalf. Please provide your parent or guardian with the referral link to get started.`
    },
    ineligibleReferOtherAdult: {
      subtitlePart1: t`Unfortunately, we cannot accept a referral on behalf of another adult. If the person you are trying to refer to care is interested in Quartet's services please have them complete the`,
      subtitlePart2: t`referral form`,
      subtitlePart3: t` or have them call Quartet directly at `,
      subtitlePart4: t` to discuss their care options.`
    },
    journey: {
      title: t`Journey`,
      main: t`What are you dealing with?`,
      subtitle: t`Answer some questions about your mental health.`
    },
    thankYou: {
      title: t`Thank you for sharing those details with us.`,
      subtitle: t`The information that you provide will help us better match you to care.`
    },
    thankYouForSharing: {
      title: t`Thank You for Sharing that with us`,
      subtitle: t`Thank You for Sharing that with us`
    },
    assessmentIntro: {
      title: t`We are now going to ask you a series of simple questions to help us understand additional needs.`,
      subtitle: t`This will be quick and take only a few minutes.`
    },
    assessmentPage: {
      title: t`Thank you for sharing those details with us.`,
      subtitle: t`Thank you for sharing those details with us.`,
      whyDoWeAsk: {
        title: t`Why do we ask?`,
        gad: t`The GAD assessment can help quantify symptoms of anxiety and establish a baseline for us to recommend care options.`,
        phq: t`The PHQ assessment can help quantify your depression symptoms and establish a baseline for us to recommend care options.`,
        auditC2: t`The AUDIT-C plus 2 assessment can help quantify symptoms of substance use (alcohol, marihuana, and prescription drugs) and establish a baseline for us to recommend care options.`
      }
    },
    crisisPage: {
      title: t`Your safety is important to us.`,
      subtitle: t`<span>Many people experience this kind of thinking and we can help. However, <strong>Quartet is not a crisis intervention</strong>. If you are in emotional distress or thinking about hurting yourself or others, please make use of these resources:</span>`,
      resources: {
        local: t`<span>Visit a <strong>local resource</strong></span>`,
        text: t`<span>Text the <strong>Crisis Text Line</strong></span>`,
        call: t`<span>Call the <strong>National Suicide Prevention Lifeline</strong></span>`
      }
    },
    supportPage: {
      title: t`Get the support you need and deserve`,
      nonPositiveAnswers: t`Although not a diagnosis, below are your results. If these scores raise any concerns for you, sharing these results with someone you trust can be a great place to start.`
    },
    supportSeparator: {
      title: t`Support`,
      main: t`What kind of help are you looking for?`,
      subtitle: t`Help us understand what sort of treatment and providers are best for you.`
    },
    treatmentConsideration: {
      title: t`Treatment Consideration`,
      heading: t`What type of treatment/services are you considering?`,
      subtitle: t`Select all that apply to you`,
      bottomNote: t`If Couples / Family Therapy is selected, a Quartet Care Navigator will need to obtain verbal consent by phone from all parties over the age of 18 or not in guardianship of the patient prior to matching to a behavioral health provider.`,
      answerDiagnosis: t`For diagnosis`,
      answerMedication: t`To see if medication may be helpful`,
      answerTesting: t`For psychological / neuropsychological testing`,
      answerIndividual: t`Individual Therapy`,
      answerGroup: t`Therapy with Family and/or a partner`,
      answerStartMedication: t`Starting medication to support mental health`,
      answerCurrentMedication: t`Currently on medication that requires a new provider to prescribe`,
      answerDrugMedication: t`Medication to treat an alcohol or drug use disorder`,
      answerTreatmentOther: t`I'm unsure and would like to learn more`
    },
    treatmentMedium: {
      title: t`Treatment Medium`,
      heading: t`How would you like to meet with your provider?`,
      answerNoPreference: t`No Preference`,
      answerInPerson: t`In-person`,
      answerVirtually: t`Virtually`,
      answerBoth: t`Both, in-person and virtually`
    },
    providerGenderIdentity: {
      title: t`Provider Gender Identity`,
      heading: t`Would you prefer a provider of a certain gender identity?`,
      answerMan: t`Man`,
      answerWoman: t`Woman`,
      answerTgMan: t`Transgender man`,
      answerTgWoman: t`Transgender woman`,
      answerNb: t`Non-binary or non-conforming`,
      answerNoPreference: t`No Preference`
    },
    providerLanguage: {
      title: t`Would you prefer a provider that speaks a different language?`,
      answerNoPreference: t`No Preference`
    },
    submitting: {
      title: t`Submitting`,
      subtitle: t`This may take a moment!`
    },
    createPatientAccount: {
      title: t`Create Account`,
      heading: t`Let's create an account`,
      subtitle: t`To confirm your appointment request, please create a password for your Quartet account.`
    },
    loadingMatches: {
      loading: t`Working on your matches!`
    },
    selfService: {
      title: t`Recommended care options`,
      subheading: t`Below are our suggested mental health providers that best match your
          clinical needs and treatment preferences.`,
      disclaimer: t`While Quartet does match you according to your insurance, it is encouraged to contact your insurance plan directly to confirm that your selected provider is in-network and review any copay and/or deductible that may apply.`,
      excludedPreferences: t`In order to provide you with best possible matches that meet your needs, we removed some of your provider preferences. We've removed`,
      requestAppointment: t`Request an appointment`,
      specialties: t`Specialties`,
      virtualSessions: t`Virtual sessions`,
      inPersonSessions: t`In-person sessions`,
      subtitle: t`We're working on your matches`,
      showMore: t`Show more`,
      showLess: t`Show less`,
      providerLanguage: t`provider language`,
      providerGender: t`provider gender`,
      providerTelePreference: t`telepreference`,
      providerAdditionalConcerns: t`additional concerns`,
      qmgDisclaimer: t`Our referral platform may recommend Quartet Medical, an in-network clinic that is affiliated with this platform, based on objective criteria and performance metrics, including but not limited to patient satisfaction and clinical outcomes. Any recommendations of our referral platform are not a substitute for professional medical advice or referrals from your healthcare provider.`
    },
    fromReactivePath: {
      start: {
        title: t`Let's complete your information`,
        subtitle: t`We'll need you to review some basic details submitted by your provider`
      },
      confirmYourIdentity: {
        title: t`Please confirm your identity`,
        subtitle: t`We care about your security and want to ensure we can find you in our systems`,
        activationCodePlaceholder: t`Activation Code`,
        dobPlaceholder: t`Patient Date of Birth`,
        subtitlePractice: t`We care about your privacy. Please enter the code sent to the email and/or sms indicated by your referring provider in order to confirm the remaining details needed to get you to care. If you have any difficulty finding your message, please check your spam folder, the contact information on file at your provider's office, or contact us at (877) 258-4010.`,
        subtitleViewMatchList: t`We care about your privacy. Please enter the code sent to the email and/or sms indicated by your referring provider in order to review and select a care option. If you have any difficulty finding your message, please check your spam folder, the contact information on file at your provider's office, or contact us at (877) 258-4010.`
      },
      unresolvedPatientError: {
        title: t`We're sorry,`
      },
      selectPrimaryInsurance: {
        title: t`Can you confirm the right primary insurance plan?`,
        subtitle: t`We found a different insurance plan in our records.`,
        entityDetectedInsurance: t`Found by Quartet`,
        primaryInsurance: t`Submitted by provider`,
        continue: t`Continue`,
        none: t`None are correct`,
        error: t`Please select a primary insurance`
      },
      secondaryInsurance: {
        title: t`Would you like to keep the other plan as a secondary insurance?`
      }
    }
  }
});

export default medicaidCopy;
